// 配件模块 C1
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import clsx from 'clsx'
import { SwiperSlide } from 'swiper/react'
import PdMedia from '@components/pd-media/pd-media'
import PdDisplaySwiperPanel from '@components/pd-display-swiper-panel/pd-display-swiper-panel'
import { IAccessoriesPanel, paddingTopClassMap, paddingBottomClassMap } from '@common/pd-meta-components-types'
import TitleAndDesc from '../../title-and-desc'
import AngleRight from '@/components/pd-angle-right/pd-angle-right'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import { ProductAccessoryListResponse } from '@/service/accessories.service'
import { getProductAccessoryList } from '@/service/accessories.service'

interface IAccessoriesDetail {
  data?: IAccessoriesPanel['dataList'][0]
  show: boolean
  beforeClose: () => void
}

const AccessoriesDetail: FC<IAccessoriesDetail> = ({ data, show, beforeClose }) => {
  const t = useTranslations()
  const accessoriesDetailRef = useRef<HTMLDivElement>(null)
  const swiperRef = useRef<{
    onChangeSlide: (index: number) => void
  }>()

  function closeDialog() {
    accessoriesDetailRef.current?.scrollTo({
      top: 0,
    })

    beforeClose()
  }

  useEffect(() => {
    show && swiperRef.current?.onChangeSlide?.(0)
  }, [show])

  return (
    <div
      className={clsx(
        'fixed top-0 left-0 right-0 w-screen h-screen z-max overflow-y-scroll',
        'bg-black/50',
        show ? 'animate-show-background block' : 'animate-hidden-background hidden'
      )}
      onClick={() => closeDialog()}
    >
      <div
        ref={accessoriesDetailRef}
        className={clsx(
          'absolute bottom-0 right-0 w-full h-auto max-h-full bg-white',
          show ? 'animate-ease-show-from-bottom' : 'animate-ease-hidden-to-bottom'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* <div className="flex justify-between">
          <h2 className="text-2xl laptop:text-4xl font-semibold">{data?.name}</h2>
          <img
            className="block w-6 h-6 laptop:w-10 laptop:h-10 cursor-pointer"
            src="/icons/icon_close.svg"
            alt="close"
            onClick={() => closeDialog()}
          />
        </div> */}
        <img
          className="block w-6 h-6 cursor-pointer absolute z-10 right-3 top-3"
          src="/icons/icon_close.svg"
          alt="close"
          onClick={() => closeDialog()}
        />
        <div className={clsx('relative')}>
          <PdDisplaySwiperPanel
            onRef={swiperRef}
            autoplay={true}
            loop={true}
            grabCursor
            navigation={false}
            pagination={{
              el: '.pd-pagination-container-flex-banner-accessories-detail',
            }}
          >
            {data?.imageUrlList?.map((item, index) => (
              <SwiperSlide key={index} className="w-full laptop:!w-fit">
                <div className={['w-full h-281px flex-none bg-gray-100 overflow-y-scroll'].join(' ')}>
                  <PdMedia src={item || ''} object="contain" mask={false} />
                </div>
              </SwiperSlide>
            ))}
          </PdDisplaySwiperPanel>
          <div
            className={clsx(
              'absolute left-0 !bottom-3 z-10',
              'pd-pagination-container_black pd-pagination-container-flex-banner-accessories-detail'
            )}
          />
        </div>

        <div className={clsx('p-6 bg-inherit')}>
          <div className={['mb-3 w-full'].join(' ')}>
            <div
              className={clsx(
                'flex-none flex items-center',
                'rounded-sm border border-zinc-300 text-black/60 text-sm leading-snug',
                'px-1.5 py-0.5 w-fit mb-3'
              )}
            >
              {data?.categoryTagName}
            </div>

            <h3 className={clsx('text-black/90 font-semibold ellipsis-2 text-lg')}>{data?.name}</h3>
          </div>
          <p className={clsx('text-black/60 text-15 mb-3 font-normal')}>{data?.desc}</p>
          <a
            className={['flex items-center text-center text-blue-600 text-sm leading-snug cursor-pointer'].join(' ')}
            href={'/accessory/' + data?.partId}
          >
            {t('KnowMore')}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 4L10 8L6 12" stroke="#0066FF" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  )
}

const AccessoriesPanel: FC<IAccessoriesPanel> = ({
  theme,
  title,
  titleTag,
  desc,
  productCode,
  mediaBorderRadius,
  mediaH5BorderRadius,
  paddingTop = 'px120',
  paddingBottom = 'px120',
}) => {
  const t = useTranslations()
  const swiperRef = useRef<{
    onChangeSlide: (index: number) => void
  }>()
  const [activeKey, setActiveKey] = useState<string>('')
  const [isMobileDevice, setMobileDevice] = useState<boolean>(false)
  const [accessoriesMeta, setAccessoriesMeta] = useState<Omit<IAccessoriesDetail, 'beforeClose'>>({ show: false })
  const { data } = useSWR<ProductAccessoryListResponse>(getProductAccessoryList.swrkey(productCode), (url) =>
    getProductAccessoryList(productCode)
  )

  const currentActiveItem = useMemo(() => data?.list?.find((item) => item.partId === activeKey), [activeKey])

  useEffect(() => {
    setMobileDevice(window?.innerWidth < 1024)
  }, [])

  useEffect(() => {
    if (data?.list?.length) {
      setActiveKey(data?.list?.[0]?.partId)
      swiperRef.current?.onChangeSlide(0)
    }
  }, [data?.list])

  useEffect(() => {
    swiperRef.current?.onChangeSlide?.(0)
  }, [activeKey])

  return (
    <>
      <div
        className={clsx(
          'w-full flex flex-col items-center',
          paddingTopClassMap[paddingTop],
          paddingBottomClassMap[paddingBottom],
          `pd-theme-${theme} bg-container-primary`
        )}
      >
        <TitleAndDesc title={title} desc={desc} titleTag={titleTag} />
        <div
          className={[
            'laptop:w-300 w-full flex laptop:flex-row flex-col justify-between gap-6 laptop:gap-12',
            'px-6 laptop:px-0',
          ].join(' ')}
        >
          {!isMobileDevice && (
            <>
              <div
                className={clsx('w-148 h-444px flex-none relative overflow-hidden', 'flex')}
                style={{ borderRadius: mediaBorderRadius ? `${mediaBorderRadius}px` : '0' }}
              >
                <PdDisplaySwiperPanel
                  onRef={swiperRef}
                  autoplay={true}
                  loop={true}
                  grabCursor
                  navigation={false}
                  pagination={{
                    el: '.pd-pagination-container-flex-banner-accessories-panel',
                  }}
                >
                  {currentActiveItem?.imageUrlList?.map((item, index) => (
                    <SwiperSlide key={index} className="w-full laptop:!w-fit">
                      <div
                        className={[
                          'w-full h-[58.67vw] laptop:w-148 laptop:h-444px flex-none bg-container-secondary',
                        ].join(' ')}
                      >
                        <PdMedia src={item || ''} object="contain" mask={false} />
                      </div>
                    </SwiperSlide>
                  ))}
                </PdDisplaySwiperPanel>
                <div
                  className={clsx(
                    'absolute left-0 !bottom-6 z-10',
                    'pd-pagination-container pd-pagination-container-flex-banner-accessories-panel'
                  )}
                />
              </div>

              <div className={clsx('flex flex-col justify-between flex-1')}>
                <div className="h-166px">
                  <div
                    className={[
                      'flex justify-between items-center mb-3 laptop:gap-4 laptop:mb-4 w-full laptop:w-148',
                    ].join(' ')}
                  >
                    <h3
                      className={clsx('text-fc-primary laptop:text-2xl font-semibold leading-[1.2] ellipsis-2')}
                      title={currentActiveItem?.name}
                    >
                      {currentActiveItem?.name}
                    </h3>

                    <span
                      className={[
                        'flex-none flex items-center rounded-sm border border-zinc-300 px-3 py-0.5 text-fc-secondary text-sm leading-snug',
                      ].join(' ')}
                    >
                      {currentActiveItem?.categoryTagName}
                    </span>
                  </div>
                  <p
                    className={[
                      'ellipsis-3 text-fc-secondary laptop:text-sm text-16 mb-3 laptop:leading-snug laptop:mb-2',
                    ].join(' ')}
                    title={currentActiveItem?.desc}
                  >
                    {currentActiveItem?.desc}
                  </p>
                  <a
                    className={[
                      'w-fit flex items-center text-center text-brand-primary text-sm leading-snug cursor-pointer',
                      'hover:brand-hover active:brand-active',
                      'group',
                    ].join(' ')}
                    href={'/accessory/' + currentActiveItem?.partId}
                  >
                    {t('KnowMore')}
                    <AngleRight
                      theme="PUDUBLUE"
                      size="4x8"
                      pathClassName="group-hover:stroke-brand-hover group-active:stroke-brand-active"
                    />
                  </a>
                </div>

                <div
                  className={clsx('grid grid-cols-5 gap-4', (data?.list?.length || 0) > 5 ? 'grid-rows-2' : 'grid-rows-1')}
                >
                  {data?.list?.map((item, index) => (
                    <div
                      key={item.partId}
                      className={[
                        'border w-full h-118px cursor-pointer overflow-hidden',
                        activeKey === item.partId
                          ? 'bg-container-secondary border-gray-100'
                          : 'bg-container-primary border-gray-300',
                      ].join(' ')}
                      style={{ borderRadius: mediaBorderRadius? `${mediaBorderRadius / 2}px` : '0' }}
                      onClick={() => {
                        setActiveKey(item.partId)
                        swiperRef.current?.onChangeSlide(index)
                      }}
                    >
                      <PdMedia src={item.imageUrl} mask={false} object="contain" className="pointer-events-none" />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {isMobileDevice && (
            <div className={clsx('grid grid-cols-2 gap-3')}>
              {data?.list?.map((item, index) => (
                <div
                  key={item.partId}
                  className={clsx('bg-container-secondary pt-2 px-2 pb-10px relative flex flex-col overflow-hidden')}
                  style={{ borderRadius: mediaH5BorderRadius ? `${mediaH5BorderRadius}px` : '0' }}
                >
                  <div
                    className='absolute right-4 top-4 w-5 h-5 bg-[url("/icons/icon_unfold.svg")] bg-contain bg-no-repeat'
                    onClick={() => {
                      setAccessoriesMeta({
                        show: true,
                        data: item,
                      })
                    }}
                  />

                  <div className={clsx('w-full h-40 flex-none')}>
                    <PdMedia src={item.imageUrl} mask={false} object="contain" className="pointer-events-none" />
                  </div>

                  <div className="flex-1 flex items-center justify-center mt-1">
                    <p className={clsx('w-full text-center text-fc-primary text-15 font-semibold leading-5 break-words')}>
                      {item.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AccessoriesDetail
        show={accessoriesMeta.show}
        data={accessoriesMeta.data}
        beforeClose={() => setAccessoriesMeta((pre) => ({ ...pre, show: false }))}
      />
    </>
  )
}

export default AccessoriesPanel
